/**
 * React Query hooks for Practice Better booking system (v2)
 *
 * Improvements:
 * - Smart polling (pauses when not needed)
 * - Better error handling with error codes
 * - Retry configuration
 * - Correlation ID support
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api/booking';

// ============================================================================
// Error codes from backend
// ============================================================================

export const ERROR_CODES = {
  SLOT_UNAVAILABLE: 'SLOT_UNAVAILABLE',
  BOOKING_IN_PROGRESS: 'BOOKING_IN_PROGRESS',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
};

// ============================================================================
// API Functions
// ============================================================================

function generateCorrelationId() {
  return Math.random().toString(36).substring(2, 10);
}

async function fetchAvailability(startDate, days = 14) {
  const correlationId = generateCorrelationId();

  const response = await fetch(
    `${API_BASE}/availability?start_date=${startDate}&days=${days}`,
    {
      headers: {
        'X-Correlation-ID': correlationId,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch availability' }));
    throw new Error(error.detail || 'Failed to fetch availability');
  }

  return response.json();
}

async function fetchAvailabilityForDate(date) {
  const correlationId = generateCorrelationId();

  const response = await fetch(
    `${API_BASE}/availability/${date}`,
    {
      headers: {
        'X-Correlation-ID': correlationId,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch availability' }));
    throw new Error(error.detail || 'Failed to fetch availability');
  }

  return response.json();
}

async function bookSession(request) {
  const correlationId = generateCorrelationId();

  // Attach auth token if present so backend can advance the logged-in user
  // regardless of the email typed in the booking form
  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  const headers = {
    'Content-Type': 'application/json',
    'X-Correlation-ID': correlationId,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/book`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      detail: 'Booking failed'
    }));

    // Create error with status code info
    const errorMessage = error.detail || 'Booking failed';
    const bookingError = new Error(errorMessage);
    bookingError.statusCode = response.status;
    bookingError.isSlotUnavailable = response.status === 409;

    throw bookingError;
  }

  return response.json();
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all availability for the next N days
 * Supports smart polling - can be paused when not needed
 */
export function useAvailability(startDate, days = 14, options = {}) {
  const {
    enabled = true,
    refetchInterval = 60 * 1000, // Default 1 minute
  } = options;

  return useQuery({
    queryKey: ['availability', startDate, days],
    queryFn: () => fetchAvailability(startDate, days),
    staleTime: 30 * 1000, // Consider data stale after 30 seconds
    refetchInterval: enabled ? refetchInterval : false,
    enabled,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

/**
 * Fetch availability for a specific date
 */
export function useAvailabilityForDate(date) {
  return useQuery({
    queryKey: ['availability', 'date', date],
    queryFn: () => fetchAvailabilityForDate(date),
    enabled: !!date,
    staleTime: 30 * 1000,
    retry: 2,
  });
}

/**
 * Book a session
 * Automatically invalidates availability queries on success
 * Handles slot unavailable errors specially
 */
export function useBookSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bookSession,
    onSuccess: () => {
      // Invalidate availability queries so they refetch
      queryClient.invalidateQueries({ queryKey: ['availability'] });
    },
    retry: (failureCount, error) => {
      // Don't retry slot unavailable errors
      if (error.isSlotUnavailable) return false;
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
  });
}

/**
 * Custom hook for managing booking state with smart polling
 */
export function useBookingFlow() {
  const [isOnFormStep, setIsOnFormStep] = useState(false);

  // Pause polling when user is filling out the form
  const shouldPoll = !isOnFormStep;

  const enterFormStep = useCallback(() => setIsOnFormStep(true), []);
  const exitFormStep = useCallback(() => setIsOnFormStep(false), []);

  return {
    shouldPoll,
    enterFormStep,
    exitFormStep,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Wall-clock date (YYYY-MM-DD) of an instant in the browser's timezone.
 * Slot instants arrive as UTC ISO strings; grouping by their raw date-prefix would file
 * late-afternoon Pacific slots under the NEXT day (>=5pm PDT / >=4pm PST cross midnight UTC).
 */
export function localYmd(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA'); // en-CA formats as YYYY-MM-DD
}

/**
 * Get user's timezone using Intl API
 */
export function detectTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) return tz; // some browsers / privacy extensions resolve this to null/empty
  } catch { /* fall through to default */ }
  return 'America/New_York'; // Better default than UTC for US-based service
}

/**
 * Format a date string for display
 */
export function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a time string for display
 */
export function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format a datetime range for display
 */
export function formatTimeRange(startTime, endTime) {
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

/**
 * Group slots by date for calendar display
 * Deduplicates slots with the same start time (picks first available consultant)
 * Sorts slots by time within each date
 * Filters to only include dates within maxDays from startDate
 */
export function groupSlotsByDate(slots, startDate = null, maxAvailableDays = null) {
  // Group all slots by date first, then take the first N dates with availability
  const grouped = slots.reduce((acc, slot) => {
    // Bucket by the slot's date in the BROWSER's zone — times are displayed browser-local,
    // so the day column must agree (the raw ISO prefix is the UTC date, not the local one).
    const date = localYmd(slot.start_time);
    
    // Only include dates from startDate onward
    if (startDate) {
      const slotDate = new Date(date);
      const start = new Date(startDate);
      if (slotDate < start) return acc;
    }
    
    if (!acc[date]) {
      acc[date] = [];
    }
    
    // Deduplicate by exact start_time
    const existingSlotIndex = acc[date].findIndex(
      s => s.start_time === slot.start_time
    );
    if (existingSlotIndex === -1) {
      acc[date].push(slot);
    }
    
    return acc;
  }, {});
  
  // Sort slots within each date by start time
  Object.keys(grouped).forEach(date => {
    grouped[date].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  });
  
  // If maxAvailableDays is set, keep only the first N dates (rolling window)
  if (maxAvailableDays) {
    const sortedDates = Object.keys(grouped).sort();
    const allowedDates = new Set(sortedDates.slice(0, maxAvailableDays));
    for (const date of Object.keys(grouped)) {
      if (!allowedDates.has(date)) {
        delete grouped[date];
      }
    }
  }
  
  return grouped;
}

/**
 * Get today's date in YYYY-MM-DD format, in the browser's timezone.
 * (toISOString would give the UTC date — after 5pm PDT that's already "tomorrow",
 * which made evening visitors' availability window start a day late.)
 */
export function getTodayString() {
  return localYmd(new Date());
}

/**
 * Check if a slot is still valid (not in the past)
 */
export function isSlotValid(slotStartTime) {
  try {
    const slotTime = new Date(slotStartTime);
    const now = new Date();
    return slotTime > now;
  } catch {
    return false;
  }
}

/**
 * Check if error is a slot unavailable error
 */
export function isSlotUnavailableError(error) {
  if (error instanceof Error) {
    return error.isSlotUnavailable === true || error.statusCode === 409;
  }
  return false;
}
